import React, { useContext, useEffect, useMemo, useState } from "react"
import { View } from "react-native"
import ReactMarkdown from "react-markdown"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"
import { DimensionsContext } from "@common/utils/mobileUtils"
import {
    normalizeEnonceLatex,
    remarkEnonceLatex,
} from "@common/utils/enonceLatex"
import { ImageViewerModal } from "@common/camera/CameraImagesM"
import { ensureRichTextVendorCss } from "../../design-system/molecules/RichText/richTextVendorCss"
import "./MarkdownWithLatex.web.css"

// La feuille de KaTeX et les polices de l'énoncé (KaTeX, PT Serif : ~0,55 Mo
// inlinés) sont celles du <RichText> du design system, posées par
// ensureRichTextVendorCss — jamais importées ici : elles pèseraient sur le
// bundle principal.

const DEFAULT_FONT_FAMILY = "'PT Serif', serif"
const HANDWRITTEN_FONT_FAMILY =
    "'Kalam', 'Comic Sans MS', 'Trebuchet MS', sans-serif"

// className is allowlisted only when it matches /^enonce-/ so AI/tutor can opt
// into the curated semantic palette without opening a CSS injection surface.
// `style` stays blocked. defaultSchema already strips <script>, on* handlers,
// and javascript: URLs.
// Order matters: rehype-raw → rehype-sanitize → rehype-katex (so KaTeX's own
// MathML/HTML output is trusted and not stripped). The math/math-inline/
// math-display tokens are remark-math's intermediate markers — on <code>
// since remark-math 6 — and must survive sanitize so rehype-katex can find
// and render them: `math-display` is what makes a `$$…$$` written inside a
// line render as a block (see remarkEnonceLatex).
const ENONCE_CLASSNAME_PATTERN = /^enonce-(primary|accent|success|error|highlight)$/
const MATH_CLASSNAME_PATTERN = /^math(-(inline|display))?$/
// Exporté pour que les autres surfaces qui rendent du Markdown+LaTeX (les
// bulles de dialogue) appliquent EXACTEMENT la même politique de nettoyage :
// deux schémas qui divergent, c'est une surface d'injection qui s'ouvre d'un
// côté sans qu'on le voie de l'autre.
export const SANITIZE_SCHEMA = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), "figure", "figcaption"],
    attributes: {
        ...(defaultSchema.attributes || {}),
        code: [["className", /^language-./, MATH_CLASSNAME_PATTERN]],
        span: [
            ...(defaultSchema.attributes?.span || []),
            ["className", ENONCE_CLASSNAME_PATTERN, MATH_CLASSNAME_PATTERN],
        ],
        div: [
            ...(defaultSchema.attributes?.div || []),
            ["className", ENONCE_CLASSNAME_PATTERN, MATH_CLASSNAME_PATTERN],
        ],
    },
}

/**
 * Web renderer for énoncé Markdown+LaTeX.
 *
 * Stack: react-markdown + remark-math + rehype-raw + rehype-sanitize + rehype-katex.
 * The five CSS variables on the wrapper are the customization seam for P-1213.
 * `flatListProps` is accepted as a no-op for call-sites that haven't migrated.
 */
// react-markdown wraps inline content in <p>, so `![](url)` on its own line
// renders as <p><figure>…</figure></p> — invalid HTML (figure can't nest in p).
// Unwrap the <p> when its only child is an image.
function ParagraphRenderer({ node, children, ...props }) {
    const onlyChild = node?.children?.length === 1 ? node.children[0] : null
    const isLoneImage =
        onlyChild?.type === "element" && onlyChild?.tagName === "img"
    if (isLoneImage) return children
    return <p {...props}>{children}</p>
}

function EnonceImage({ alt, src, onZoom }) {
    const [broken, setBroken] = useState(false)
    if (broken) {
        return (
            <figure className="masteur-enonce-figure">
                <div
                    className="masteur-enonce-broken-image"
                    role="img"
                    aria-label={alt || "image indisponible"}
                >
                    Image indisponible{alt ? ` — ${alt}` : ""}
                </div>
            </figure>
        )
    }
    return (
        <figure className="masteur-enonce-figure">
            <img
                src={src}
                alt={alt || ""}
                loading="lazy"
                onClick={() => onZoom(src)}
                onError={() => setBroken(true)}
                style={{ cursor: "zoom-in" }}
            />
            {/* L'alt générique « figure » (pipeline d'incrustation) n'est pas
                une légende — ne l'afficher que s'il décrit vraiment l'image. */}
            {alt && alt.toLowerCase() !== "figure" ? (
                <figcaption>{alt}</figcaption>
            ) : null}
        </figure>
    )
}

export default function MarkdownWithLatex({
    value = "",
    handwrittenStyle = false,
    style = {},
    // eslint-disable-next-line no-unused-vars, react/prop-types
    flatListProps,
    ...rest
}) {
    // Cap rendered width to the responsive content width (same constraint as
    // HeaderContainerM). Without this the énoncé spans the full viewport on
    // wide monitors and becomes unreadable.
    const dimensions = useContext(DimensionsContext)

    useEffect(() => {
        void ensureRichTextVendorCss()
    }, [])

    // Click-to-zoom: ImageViewerModal opens on top of the rendered markdown.
    const [viewerSrc, setViewerSrc] = useState(null)
    const components = useMemo(
        () => ({
            img: (props) => <EnonceImage {...props} onZoom={setViewerSrc} />,
            p: ParagraphRenderer,
        }),
        []
    )

    const fontFamily = handwrittenStyle
        ? HANDWRITTEN_FONT_FAMILY
        : DEFAULT_FONT_FAMILY

    const wrapperStyle = {
        "--enonce-font-family": fontFamily,
        "--enonce-font-scale": 1,
        "--enonce-line-height": 1.5,
        "--enonce-color-text": "#333",
        "--enonce-bg": "transparent",
        width: dimensions?.width ?? "100%",
        maxWidth: "100%",
    }

    return (
        <View
            style={[{ width: "100%", alignItems: "center" }, style]}
            {...rest}
        >
            <div className="masteur-enonce" style={wrapperStyle}>
                <ReactMarkdown
                    remarkPlugins={[remarkMath, remarkEnonceLatex]}
                    rehypePlugins={[
                        rehypeRaw,
                        [rehypeSanitize, SANITIZE_SCHEMA],
                        rehypeKatex,
                    ]}
                    components={components}
                >
                    {normalizeEnonceLatex(value)}
                </ReactMarkdown>
            </div>
            <ImageViewerModal
                visible={!!viewerSrc}
                onDismiss={() => setViewerSrc(null)}
                images={viewerSrc ? [viewerSrc] : []}
            />
        </View>
    )
}
